/**
 * Every document the signed-in owner holds, across their whole portfolio
 * (COL-528), plus the general ones that belong to no property.
 *
 * Backed by `owner_portfolio_documents` (20261012085007), which resolves the
 * portfolio through `owner_visible_properties()` — so this page and the
 * properties grid cannot disagree about which buildings are the owner's.
 *
 * The filing LEVEL and the SOURCE both come from the server rather than being
 * re-derived here: the per-property feed already prints the same three
 * provenance labels, and two surfaces wording the same fact differently is
 * exactly what moving Documents out of the property page risks.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

/** Where a document is filed. Mirrors the RPC's `level`. */
export type OwnerDocumentLevel =
  | "general"
  | "property"
  | "apartment"
  | "room"
  | "tenant";

/** Who put it there. Mirrors the RPC's `source`. */
export type OwnerDocumentSource = "coliviq" | "owner" | "tenant";

/**
 * Which table a row came from, and so how its `file_url` resolves.
 *
 * `apartment` and `owner` rows store a bare path in the `contracts` bucket.
 * `tenant` rows do NOT: tenant_documents.file_url may be a full URL, a
 * bucket-qualified path ("handover-protocols/…"), or a bare path in
 * `tenant-documents` — which is why resolveTenantDocumentUrl exists.
 */
export type OwnerDocumentOrigin = "apartment" | "tenant" | "owner";

export type OwnerPortfolioDocument = {
  id: string;
  level: OwnerDocumentLevel;
  title: string;
  file_url: string;
  file_name: string | null;
  content_type: string | null;
  created_at: string;
  source: OwnerDocumentSource;
  origin: OwnerDocumentOrigin;
  property_id: number | null;
  property_title: string | null;
  apartment_label: string | null;
  room_title: string | null;
  tenant_name: string | null;
  /**
   * Where the row sits, by id (COL-628, migration owner_portfolio_documents_ids), so the Dokumente
   * drill-down never has to match on labels — "Zimmer 1" repeats across a
   * building's units. Optional: until that migration has run, the RPC returns
   * none of them and the page still lists every file under Alle.
   */
  apartment_id?: number | null;
  room_id?: number | null;
  booking_id?: number | null;
  /** The tenancy's own dates and raw bookings.status, for the Mieter order. */
  tenancy_move_in?: string | null;
  tenancy_move_out?: string | null;
  tenancy_status?: string | null;
  /** bookings.admin_archived_at. Archiving leaves the status as it was, so
   *  this is what keeps an archived booking out of Aktiv/Bevorstehend. */
  tenancy_archived_at?: string | null;
  /** The caller uploaded this row. On an `apartment` origin it gates rename
   *  and remove on the Dokumente page; RLS still decides the write. */
  uploaded_by_me?: boolean | null;
};

/**
 * Keyed by account, as accountBillingAddressKey already is. Neither sign-out
 * nor impersonation clears the QueryClient, so an unscoped key hands the next
 * account the previous owner's rows while its own request is in flight — and
 * impersonation is exactly how this portal is meant to be reviewed first.
 */
export function ownerPortfolioDocumentsKey(userId: string | undefined) {
  return ["owner", "portfolio-documents", userId] as const;
}

/**
 * True when the failure is "this database has no such function" — the dev
 * project trails LIVE, and a section must not break because of it. The caller
 * degrades to an empty list, which is also what a brand-new owner sees.
 */
function isMissingFunction(error: {
  code?: string;
  message?: string;
}): boolean {
  if (error?.code === "PGRST202" || error?.code === "42883") return true;
  return /could not find the function|does not exist/i.test(
    error?.message ?? ""
  );
}

export function useOwnerPortfolioDocuments(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ownerPortfolioDocumentsKey(user?.id),
    enabled: enabled && !!user?.id,
    queryFn: async (): Promise<OwnerPortfolioDocument[]> => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (fn: string) => Promise<{
            data: unknown;
            error: { code?: string; message?: string } | null;
          }>;
        }
      ).rpc("owner_portfolio_documents");
      if (error) {
        if (isMissingFunction(error)) return [];
        throw error;
      }
      return (data ?? []) as OwnerPortfolioDocument[];
    },
  });
}

/**
 * The words a row prints for its filing level and its source.
 *
 * Kept beside the type rather than in the page so the Add-document dialog's
 * chips, the row and any later surface all read from one list — the PRD's
 * filter chips and the row's own tag are the same vocabulary.
 */
export const DOCUMENT_LEVELS: readonly OwnerDocumentLevel[] = [
  "general",
  "apartment",
  "room",
  "tenant",
] as const;

/**
 * Everything the search box matches, as one lower-cased string per row.
 *
 * The PRD ANDs multiple words ("Goethestraße Lena" narrows to both), which
 * only works against one combined haystack — matching word-by-word against
 * individual FIELDS would fail that example, because neither word is in the
 * same field as the other.
 */
export function documentHaystack(
  d: OwnerPortfolioDocument,
  levelLabel: string,
  sourceLabel: string,
  dateLabel: string
): string {
  return [
    d.title,
    d.file_name,
    d.property_title,
    d.apartment_label,
    d.room_title,
    d.tenant_name,
    levelLabel,
    sourceLabel,
    dateLabel,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** Every word must appear somewhere in the row. Empty query matches all. */
export function matchesDocumentSearch(
  haystack: string,
  query: string
): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return words.every((w) => haystack.includes(w));
}
